/**
 * Ship-to location: where the shopper is shipping to, for every delivery
 * surface — the PDP shipping line and panel (assets/pdp-delivery.js), the
 * collection "Arrives by" filter (assets/arrives-by-filter.js) — and the
 * popover that changes it (snippets/ship-to.liquid), opened from any
 * [data-ship-to-open] button: the header's pin (snippets/header-actions.liquid)
 * and the shipping panel's "Change".
 *
 * First hit wins:
 *   signed in   a choice THIS customer made in this browser → their default
 *               address → their IP
 *   signed out  a choice made signed out → their IP
 * A choice is stored in localStorage `birch:ship-to` with the customer it
 * belongs to, so a shared computer never hands one shopper's address to the
 * next. Saved addresses come from Liquid, for a signed-in customer only
 * (#BirchShipToAccount: ids, ZIP, state, street lines — no names or phones).
 * Only a saved address can be a PO box (assets/delivery-core.js reads it the
 * way checkout's Shipping Rules do); a typed ZIP never is.
 *
 * IP: birch-ai's /public/geo ({country, region, postal, city}; `postal` may
 * be absent — then the state alone). US only. Asked once per session, after
 * the page is idle, and only when nothing better is known — never on the
 * critical path. Everything here survives blocked storage and a failed fetch:
 * the surfaces simply keep the text the server rendered.
 *
 * Wording, as Amazon's header has it for a signed-out shopper ("Bellevue
 * 98005"): a location reads "City ZIP" when its city is known (place()).
 *
 * Every change fires `birch:ship-to-changed` on document (detail = the
 * location) and every surface re-renders. API: window.BirchShipTo
 * = { get(), set(loc), open(anchorEl?), place(loc) }.
 *
 * Cart ⇄ checkout. The shopper's cart carries a delivery address (Storefront
 * API, same origin, no token) and checkout opens with it filled in — and an
 * address typed IN checkout is written back onto the same cart (both checked
 * on prod 2026-10-20: 10461/NY prefilled; changed to 94110/CA in checkout,
 * the store read 94110/CA back). So:
 *   store → checkout  a location the shopper CHOSE (a ZIP, a saved address —
 *                     never the IP guess) is put on the cart: ZIP and state,
 *                     or the whole saved address
 *   checkout → store  on load, a cart address that differs from the last one
 *                     this page wrote (or read) came from checkout: it becomes
 *                     the choice
 * `birch:ship-to-cart` remembers the last cart address seen or written, per
 * cart token. Nothing here waits on the network: the read runs when idle.
 */
(function () {
  'use strict';

  var D = window.BirchDelivery;
  if (!D || window.BirchShipTo) return;

  var STORE_KEY = 'birch:ship-to';
  var GEO_KEY = 'birch:geo';
  var CART_KEY = 'birch:ship-to-cart';
  var SF_API = 'api/2025-10/graphql.json';
  var EVENT = 'birch:ship-to-changed';

  // Outside the App Proxy path, so the storefront names the worker host per
  // environment: the staging storefront and local dev → the staging worker,
  // everything else → production. window.Theme.geoUrl overrides.
  var PROD_GEO = 'https://birch-ai-edge.api.birchstore.com/public/geo';
  var STAGING_GEO = 'https://birch-ai-edge.api-staging.birchstore.com/public/geo';
  var NON_PROD_HOST_RX = /(^|\.)birch-staging\.myshopify\.com$|^(localhost|127\.0\.0\.1|.*\.local)$/i;

  function geoUrl() {
    if (window.Theme && window.Theme.geoUrl) return window.Theme.geoUrl;
    return NON_PROD_HOST_RX.test(window.location.hostname) ? STAGING_GEO : PROD_GEO;
  }

  // Safari private mode and blocked site data throw on storage access.
  function readStore(kind, key) {
    try {
      var raw = window[kind].getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (_err) {
      return null;
    }
  }

  function writeStore(kind, key, value) {
    try {
      window[kind].setItem(key, JSON.stringify(value));
    } catch (_err) {
      /* non-fatal: the choice lasts this page only */
    }
  }

  function readAccount() {
    var el = document.getElementById('BirchShipToAccount');
    if (!el) return null;
    try {
      var a = JSON.parse(el.textContent);
      return a && a.customerId ? a : null;
    } catch (_err) {
      return null;
    }
  }

  var account = readAccount();

  function addressById(id) {
    var list = (account && account.addresses) || [];
    for (var i = 0; i < list.length; i++) if (String(list[i].id) === String(id)) return list[i];
    return null;
  }

  function fromAddress(a) {
    if (!a) return null;
    var zip = D.zip5(a.zip);
    var state = String(a.state || '').toUpperCase() || D.zipToState(zip);
    if (!state) return null;
    return { zip: zip || null, state: state, city: cityOf(a.city), source: 'address', addressId: String(a.id), po: D.isPoBoxAddress(a) };
  }

  /** A city as shown: trimmed, or null. */
  function cityOf(city) {
    var c = String(city || '').trim();
    return c || null;
  }

  /** A geo answer → a location, US only. A ZIP that disagrees with the region is dropped. */
  function fromGeo(g) {
    if (!g || g.country !== 'US' || !g.region) return null;
    var state = String(g.region).toUpperCase();
    var zip = D.zip5(g.postal);
    if (zip && D.zipToState(zip) !== state) zip = '';
    return { zip: zip || null, state: state, city: cityOf(g.city), source: 'geo', po: false };
  }

  /**
   * The stored choice, if it is this shopper's: this customer's own when
   * signed in, one made signed out when not. A saved address is re-read from
   * the account, so an address edited (or deleted) since is never stale.
   */
  function storedChoice() {
    var s = readStore('localStorage', STORE_KEY);
    if (!s || typeof s !== 'object' || !s.state) return null;
    var owner = s.customerId ? String(s.customerId) : '';
    if (owner !== (account ? String(account.customerId) : '')) return null;
    if (s.source === 'address') return fromAddress(addressById(s.addressId));
    // A typed ZIP is never a PO box; an address adopted from checkout keeps what it was.
    return { zip: D.zip5(s.zip) || null, state: String(s.state).toUpperCase(), city: cityOf(s.city), source: s.source === 'geo' ? 'geo' : 'zip', po: s.from === 'checkout' && !!s.po };
  }

  function defaultAddress() {
    return account && account.defaultId ? fromAddress(addressById(account.defaultId)) : null;
  }

  function resolveLocal() {
    return storedChoice() || defaultAddress() || fromGeo(readStore('sessionStorage', GEO_KEY));
  }

  var current = resolveLocal();

  function copy(loc) {
    if (!loc) return null;
    var out = {};
    for (var k in loc) if (Object.prototype.hasOwnProperty.call(loc, k)) out[k] = loc[k];
    return out;
  }

  function sameLoc(a, b) {
    if (!a || !b) return a === b;
    return a.zip === b.zip && a.state === b.state && a.city === b.city && !!a.po === !!b.po && a.source === b.source && a.addressId === b.addressId;
  }

  /**
   * Where a location is, in words: "Bellevue 98005" (city known), "98005",
   * else the state's name. A typed ZIP has no city.
   */
  function place(loc) {
    if (!loc) return '';
    if (loc.city && loc.zip) return loc.city + ' ' + loc.zip;
    return loc.zip || D.stateName(loc.state) || '';
  }

  function update(next) {
    var prev = current;
    current = next;
    paint();
    if (!sameLoc(prev, current)) document.dispatchEvent(new CustomEvent(EVENT, { detail: copy(current) }));
  }

  /**
   * Store a choice: {zip} (state read off it), {state}, {source: 'address',
   * addressId} or a geo location. Returns the location, or null when it
   * can't be read (an unknown ZIP).
   */
  function set(loc) {
    if (!loc) return null;
    var next;
    if (loc.source === 'address' && loc.addressId != null) {
      next = fromAddress(addressById(loc.addressId));
    } else {
      var zip = D.zip5(loc.zip);
      var state = zip ? D.zipToState(zip) : String(loc.state || '').toUpperCase();
      var geo = loc.source === 'geo';
      // Only the IP's own answer keeps its city; a typed ZIP has none.
      next = state ? { zip: zip || null, state: state, city: geo ? cityOf(loc.city) : null, source: geo ? 'geo' : 'zip', po: false } : null;
    }
    if (!next) return null;
    var stored = copy(next);
    stored.at = Date.now();
    if (account) stored.customerId = String(account.customerId);
    writeStore('localStorage', STORE_KEY, stored);
    update(next);
    if (next.source !== 'geo') pushToCart(next);
    return copy(next);
  }

  // ── Cart address (checkout's prefill) ──────────────────────────────────────

  /** The shopper's cart, from the `cart` cookie ("<token>?key=…"), or null before one exists. */
  function cartToken() {
    var m = /(?:^|;\s*)cart=([^;]+)/.exec(document.cookie || '');
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]);
    } catch (_err) {
      return m[1];
    }
  }

  function storefront(query, variables) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + SF_API, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (r) {
      return r.ok ? r.json() : null;
    });
  }

  /**
   * A cart address → "zip|state|street", the identity compared between reads
   * and writes. The street is in it so a saved address in the ZIP already on
   * the cart still goes on whole, and a street changed at checkout is seen.
   */
  function cartSig(a) {
    var street = String(a.address1 || '').trim().replace(/\s+/g, ' ').toLowerCase();
    return (D.zip5(a.zip) || '') + '|' + String(a.provinceCode || '').toUpperCase() + '|' + street;
  }

  /**
   * Whether the cart address signed `sig` already stands for `address`. A
   * ZIP-only address (a typed ZIP, or one adopted from checkout) carries no
   * street, so any street in the same ZIP and state counts: writing it would
   * only wipe the street and city the shopper gave checkout.
   */
  function cartHas(sig, address) {
    var mine = cartSig(address);
    return sig === mine || (!address.address1 && sig.indexOf(mine) === 0);
  }

  function rememberCart(token, sig) {
    writeStore('localStorage', CART_KEY, { token: token, sig: sig });
  }

  /** The address to put on the cart for a location: a saved address whole, else ZIP + state. */
  function cartAddressFor(loc) {
    var a = loc.source === 'address' ? addressById(loc.addressId) : null;
    var out = { countryCode: 'US', provinceCode: loc.state };
    if (loc.zip) out.zip = loc.zip;
    if (a) {
      if (a.address1) out.address1 = a.address1;
      if (a.address2) out.address2 = a.address2;
      if (a.city) out.city = a.city;
      if (a.company) out.company = a.company;
    }
    return out;
  }

  var pushing = null;

  // Checkout is starting: leave the cart alone until the shopper is back. The
  // address write never blocks anything, but it mustn't land on a cart that is
  // already becoming a checkout.
  var leaving = false;
  document.addEventListener('click', function (event) {
    var t = event.target && event.target.closest && event.target.closest('#checkout, [name="checkout"], a[href$="/checkout"], shopify-accelerated-checkout, .shopify-payment-button');
    if (t) leaving = true;
  }, true);
  document.addEventListener('submit', function (event) {
    var f = event.target;
    var by = event.submitter;
    if ((by && by.name === 'checkout') || (f && f.action && /\/checkout(\?|$)/.test(f.action))) leaving = true;
  }, true);
  window.addEventListener('pagehide', function () { leaving = true; });
  window.addEventListener('pageshow', function () { leaving = false; });

  /** Put a chosen location on the shopper's cart (replacing what checkout or we put there). */
  function pushToCart(loc) {
    var token = cartToken();
    if (leaving || !token || !loc || !loc.state || !window.fetch) return;
    var address = cartAddressFor(loc);
    var sig = cartSig(address);
    var last = readStore('localStorage', CART_KEY);
    if (last && last.token === token && cartHas(last.sig, address) && !pushing) return;
    var q = 'mutation($id:ID!,$a:[CartSelectableAddressInput!]!){cartDeliveryAddressesReplace(cartId:$id,addresses:$a){userErrors{code message}}}';
    pushing = storefront(q, {
      id: 'gid://shopify/Cart/' + token,
      a: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: address } }]
    })
      .then(function (d) {
        var errs = d && d.data && d.data.cartDeliveryAddressesReplace ? d.data.cartDeliveryAddressesReplace.userErrors : null;
        if (errs && !errs.length) rememberCart(token, sig);
      })
      .catch(function () {
        /* checkout simply opens without the prefill */
      })
      .then(function () {
        pushing = null;
      });
  }

  /** The location a cart address stands for: one of the account's saved addresses when it is one. */
  function fromCartAddress(a) {
    var zip = D.zip5(a.zip);
    var state = String(a.provinceCode || '').toUpperCase() || D.zipToState(zip);
    if (!state) return null;
    var list = (account && account.addresses) || [];
    for (var i = 0; i < list.length; i++) {
      var s = list[i];
      if (D.zip5(s.zip) === zip && String(s.address1 || '').trim().toLowerCase() === String(a.address1 || '').trim().toLowerCase() && a.address1) {
        return fromAddress(s);
      }
    }
    return { zip: zip || null, state: state, city: cityOf(a.city), source: 'zip', po: D.isPoBoxAddress(a) };
  }

  /**
   * Read the cart's address; one that differs from the last this browser saw
   * came from checkout → it becomes the choice. With none on the cart, a
   * chosen location goes onto it.
   */
  function pullFromCart() {
    var token = cartToken();
    if (!token || !window.fetch) return;
    var q = 'query($id:ID!){cart(id:$id){delivery{addresses{selected address{...on CartDeliveryAddress{zip provinceCode countryCode address1 address2 city company}}}}}}';
    storefront(q, { id: 'gid://shopify/Cart/' + token })
      .then(function (d) {
        var cart = d && d.data ? d.data.cart : null;
        if (!cart) return;
        var picked = null;
        var list = (cart.delivery && cart.delivery.addresses) || [];
        for (var i = 0; i < list.length; i++) if (list[i].selected && list[i].address) picked = list[i].address;
        var last = readStore('localStorage', CART_KEY);
        if (!picked || picked.countryCode !== 'US' || !picked.provinceCode) {
          if (current && current.source !== 'geo') pushToCart(current);
          return;
        }
        var sig = cartSig(picked);
        var seen = last && last.token === token ? last.sig : null;
        rememberCart(token, sig);
        if (sig === seen) return; // ours, or already adopted
        if (current && cartHas(sig, cartAddressFor(current))) return;
        var loc = fromCartAddress(picked);
        if (!loc) return;
        var stored = copy(loc);
        stored.at = Date.now();
        stored.from = 'checkout';
        if (account) stored.customerId = String(account.customerId);
        writeStore('localStorage', STORE_KEY, stored);
        update(loc);
      })
      .catch(function () {
        /* the page keeps the location it had */
      });
  }

  // ── IP lookup ──────────────────────────────────────────────────────────────

  var geoPending = null;

  /**
   * One request per session, shared by every caller. A non-US answer is
   * cached too (as itself), so a visitor abroad isn't asked again; a failed
   * request isn't, so a blip doesn't cost the rest of the session.
   */
  function lookupGeo() {
    var cached = readStore('sessionStorage', GEO_KEY);
    if (cached) return Promise.resolve(cached);
    if (geoPending) return geoPending;
    if (!window.fetch) return Promise.resolve(null);
    geoPending = fetch(geoUrl(), { credentials: 'omit' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (d) {
        if (!d || typeof d !== 'object') return null;
        var g = { country: d.country || null, region: d.region || null, postal: d.postal || null, city: d.city || null };
        writeStore('sessionStorage', GEO_KEY, g);
        return g;
      })
      .catch(function () {
        return null;
      })
      .then(function (g) {
        geoPending = null;
        return g;
      });
    return geoPending;
  }

  function whenIdle(fn) {
    var run = function () {
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 4000 });
      else setTimeout(fn, 1200);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  whenIdle(pullFromCart);
  // A first item creates the cart: put a chosen location on it straight away.
  document.addEventListener('cart:update', function () {
    if (current && current.source !== 'geo') pushToCart(current);
  });

  if (!current) {
    whenIdle(function () {
      lookupGeo().then(function (g) {
        // A choice made while the lookup was in flight wins.
        if (!current) {
          var loc = fromGeo(g);
          if (loc) update(loc);
        }
      });
    });
  }

  // ── Header buttons ─────────────────────────────────────────────────────────

  /**
   * Every [data-ship-to-open] button shows the place ("Bellevue 98005", else
   * the ZIP, else the state) in its [data-ship-to-label], and says it in its
   * accessible name. The label slot is reserved in CSS, so filling it shifts
   * nothing.
   */
  function paint() {
    var text = current ? (current.city && current.zip ? place(current) : current.zip || current.state) : '';
    var labels = document.querySelectorAll('[data-ship-to-label]');
    for (var i = 0; i < labels.length; i++) {
      var el = labels[i];
      var shown = text || el.getAttribute('data-placeholder') || '';
      if (el.textContent !== shown) el.textContent = shown;
      el.toggleAttribute('data-empty', !text);
    }
    var buttons = document.querySelectorAll('[data-ship-to-open][data-label-set]');
    for (var j = 0; j < buttons.length; j++) {
      var b = buttons[j];
      var name = current
        ? b.getAttribute('data-label-set') + ' ' + place(current)
        : b.getAttribute('data-label-unset') || '';
      if (name && b.getAttribute('aria-label') !== name) b.setAttribute('aria-label', name);
    }
  }

  // ── Popover ────────────────────────────────────────────────────────────────

  var dialog = document.getElementById('ShipToDialog');
  var opener = null;

  function $(sel) {
    return dialog ? dialog.querySelector(sel) : null;
  }

  function showError(which, on) {
    var el = $('[data-ship-to-error="' + which + '"]');
    if (el) el.hidden = !on;
    if (which === 'zip') {
      var input = $('[data-ship-to-zip]');
      if (input) {
        if (on) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }
  }

  function setExpanded(on) {
    if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', on ? 'true' : 'false');
  }

  /** Mark the chosen saved address, prefill the ZIP, clear old errors. */
  function syncDialog() {
    var buttons = dialog.querySelectorAll('[data-ship-to-address]');
    for (var i = 0; i < buttons.length; i++) {
      var chosen = !!current && current.source === 'address' && current.addressId === buttons[i].getAttribute('data-ship-to-address');
      buttons[i].setAttribute('aria-pressed', chosen ? 'true' : 'false');
    }
    var input = $('[data-ship-to-zip]');
    if (input) input.value = current && current.source !== 'address' && current.zip ? current.zip : '';
    showError('zip', false);
    showError('geo', false);
  }

  /**
   * Desktop: hang the popover under its button, inside the viewport. Phones
   * (and an open() without a button): CSS makes it a bottom sheet / centres it.
   */
  function position(anchor) {
    var anchored = !!anchor && anchor.getBoundingClientRect && window.innerWidth >= 750;
    dialog.classList.toggle('ship-to-dialog--anchored', anchored);
    if (!anchored) return;
    var r = anchor.getBoundingClientRect();
    var width = dialog.offsetWidth || Math.min(360, window.innerWidth - 32);
    var left = Math.max(16, Math.min(r.right - width, window.innerWidth - width - 16));
    var top = r.bottom + 8;
    var height = dialog.offsetHeight;
    if (height && top + height > window.innerHeight - 16) top = Math.max(16, window.innerHeight - height - 16);
    dialog.style.setProperty('--ship-to-left', left + 'px');
    dialog.style.setProperty('--ship-to-top', top + 'px');
  }

  function open(anchor) {
    if (!dialog) return;
    if (dialog.open) dialog.close();
    opener = anchor || document.activeElement;
    syncDialog();
    try {
      dialog.showModal();
    } catch (_err) {
      dialog.setAttribute('open', '');
    }
    // Measured once open — a closed <dialog> has no box.
    position(anchor);
    setExpanded(true);
    var first = $('[data-ship-to-address][aria-pressed="true"]') || $('[data-ship-to-address]') || $('[data-ship-to-zip]');
    if (first) first.focus();
  }

  function close() {
    if (dialog && dialog.open) dialog.close();
  }

  if (dialog) {
    dialog.addEventListener('close', function () {
      setExpanded(false);
      // The PDP pop-up re-renders on the change this popover just made, so a
      // "Change" button that opened it may be gone: its replacement takes focus.
      var back = opener && opener.isConnected ? opener : document.querySelector('dialog[open] [data-ship-to-open]');
      if (back && back.focus) back.focus();
      opener = null;
    });

    dialog.addEventListener('click', function (event) {
      var t = event.target;
      // A click on the backdrop lands on the <dialog> itself.
      if (t === dialog || t.closest('[data-ship-to-close]')) return close();
      var addr = t.closest('[data-ship-to-address]');
      if (addr) {
        if (set({ source: 'address', addressId: addr.getAttribute('data-ship-to-address') })) close();
        return;
      }
      var geo = t.closest('[data-ship-to-geo]');
      if (geo && !geo.disabled) {
        geo.disabled = true;
        geo.setAttribute('aria-busy', 'true');
        showError('geo', false);
        lookupGeo().then(function (g) {
          geo.disabled = false;
          geo.removeAttribute('aria-busy');
          var loc = fromGeo(g);
          if (loc && set(loc)) close();
          else showError('geo', true);
        });
      }
    });

    var form = $('[data-ship-to-form]');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var input = $('[data-ship-to-zip]');
        var zip = input ? input.value : '';
        if (D.zipToState(zip) && set({ zip: zip, source: 'zip' })) close();
        else {
          showError('zip', true);
          if (input) input.focus();
        }
      });
    }

    window.addEventListener('resize', function () {
      if (dialog.open && opener && dialog.classList.contains('ship-to-dialog--anchored')) position(opener);
    });
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-ship-to-open]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });

  // Another tab chose a location, or this page came back from the
  // back/forward cache: re-read rather than show a stale one.
  function refresh() {
    account = readAccount();
    update(resolveLocal() || (current && current.source === 'geo' ? current : null));
  }
  window.addEventListener('storage', function (event) {
    if (event.key === STORE_KEY) refresh();
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });

  window.BirchShipTo = {
    get: function () {
      return copy(current);
    },
    set: set,
    open: open,
    place: place
  };

  paint();
})();
